import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * A destructive-action confirmation modal with a type-to-confirm guard.
 *
 * Shows a warning describing the consequences (`children`) and keeps the confirm
 * button disabled until the user types `confirmWord` (case-insensitive), so
 * hard-to-undo actions can't be triggered by a stray click.
 *
 * @param {boolean}  open         - Whether the dialog is visible.
 * @param {string}   title        - Dialog heading.
 * @param {string}   confirmWord  - Word the user must type to enable confirm.
 * @param {string}   confirmLabel - Text on the confirm button.
 * @param {string}   confirmColor - MUI color for the confirm button (default 'error').
 * @param {boolean}  loading      - Disables inputs and shows a spinner while pending.
 * @param {string}   error        - Optional error message shown inside the dialog.
 * @param {boolean}  blocked      - When true, the action can't proceed: the
 *   type-to-confirm field and confirm button are hidden, leaving only the
 *   explanatory `children` and a Close button. Use to explain why an action is
 *   unavailable (e.g. a dependency still in use).
 * @param {Function} onConfirm    - Called when the user confirms.
 * @param {Function} onClose      - Called when the user cancels / dismisses.
 * @param {React.ReactNode} children - Warning body describing what will happen.
 */
export default function ConfirmActionDialog({
  open,
  title,
  confirmWord,
  confirmLabel,
  confirmColor = 'error',
  loading = false,
  error = null,
  blocked = false,
  onConfirm,
  onClose,
  children,
}) {
  const { t: translate } = useTranslation();
  const [value, setValue] = useState('');

  useEffect(() => {
    setValue('');
  }, [open]);

  // Hold the content-defining props stable through the close transition: callers
  // derive them from a selection that resets to null on close, so without this the
  // dialog flips mode or blanks a name for the length of the fade-out.
  const frozen = useRef({ title, confirmWord, confirmLabel, confirmColor, blocked, children });
  if (open) {
    frozen.current = { title, confirmWord, confirmLabel, confirmColor, blocked, children };
  }
  const view = open
    ? { title, confirmWord, confirmLabel, confirmColor, blocked, children }
    : frozen.current;

  const matches = value.trim().toLowerCase() === (view.confirmWord || '').toLowerCase();
  const canConfirm = matches && !loading;

  const handleConfirm = () => {
    if (canConfirm) onConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color={view.confirmColor === 'error' ? 'error' : 'warning'} />
        {view.title}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>{view.children}</Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!view.blocked && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {translate('shell.confirmInstruction', { word: view.confirmWord })}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleConfirm(); }}
              placeholder={view.confirmWord}
              disabled={loading}
              autoComplete="off"
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          {view.blocked ? translate('shell.close') : translate('shell.cancel')}
        </Button>
        {!view.blocked && (
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            color={view.confirmColor}
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {view.confirmLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
